import {
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiArchiveOutline,
  mdiArrowExpand,
  mdiBarrel,
  mdiCurrencyUsd,
  mdiExclamationThick,
  mdiFormatListBulleted,
  mdiInvoiceImportOutline,
  mdiPackageVariantClosedPlus,
  mdiReceiptTextOutline,
  mdiSwapHorizontal,
} from "@mdi/js";
import { Grid, Paper, Tooltip } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { HybridRouteButtonLink } from "../../../../components/molecules/HybridRouteButtonLink";
import { ForbiddenError } from "../../../../errors/ForbiddenError";

interface RequestFormButtonProps {
  icon: string;
  isReact?: boolean;
  name: string;
  tooltip: string;
  url: string;
}

const RequestFormButton: React.FC<RequestFormButtonProps> = ({
  icon,
  isReact = true,
  name,
  tooltip,
  url,
}) => {
  return (
    <Grid
      container
      size={{ md: 4, sm: 6, xs: 12 }}
      sx={{
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Tooltip arrow title={tooltip}>
        <HybridRouteButtonLink
          color="slate"
          isReact={isReact}
          size="large"
          startIcon={null}
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            height: "136px",
            justifyContent: "center",
            width: "100%",
          }}
          url={url}
          variant="outlined"
        >
          <Icon icon={icon} sx={{ fontSize: "3rem" }} />
          {name}
        </HybridRouteButtonLink>
      </Tooltip>
    </Grid>
  );
};

const CreateNewRequest: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer maxWidth="lg" title="New Request">
      <Paper
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          padding: "32px",
          width: "100%",
        }}
        variant="outlined"
      >
        <Grid container spacing={4}>
          <RequestFormButton
            icon={mdiSwapHorizontal}
            name="Change Material"
            tooltip={t("mmRequests.createNewRequest.tooltips.changeMaterial")}
            url="/vbuyer/change_material_requests/new"
          />

          <RequestFormButton
            icon={mdiExclamationThick}
            name="Issues Of RFQ"
            tooltip={t("mmRequests.createNewRequest.tooltips.issuesOfRFQ")}
            url="/vbuyer/issues_of_rfq_requests/new"
          />

          <RequestFormButton
            icon={mdiReceiptTextOutline}
            name="Other Masterdata Services"
            tooltip={t(
              "mmRequests.createNewRequest.tooltips.otherMasterdataServices",
            )}
            url="/vbuyer/other_masterdata_services_requests/new"
          />

          <RequestFormButton
            icon={mdiFormatListBulleted}
            name="Bill Of Materials Mgmt"
            tooltip={t(
              "mmRequests.createNewRequest.tooltips.billOfMaterialsMgmt",
            )}
            url="/vbuyer/bill_of_materials_mgmt_requests/new"
          />

          <RequestFormButton
            icon={mdiPackageVariantClosedPlus}
            name="Register New Material and SPIR"
            tooltip={t(
              "mmRequests.createNewRequest.tooltips.registerNewMaterial",
            )}
            url="/vbuyer/rnm_requests/new"
          />

          <RequestFormButton
            icon={mdiBarrel}
            isReact={false}
            name="REPETRO Registration"
            tooltip={t("mmRequests.createNewRequest.tooltips.mrd")}
            url="/vbuyer/mrd_requests/new"
          />

          <RequestFormButton
            icon={mdiArchiveOutline}
            isReact={false}
            name="MRP"
            tooltip={t("mmRequests.createNewRequest.tooltips.mrp")}
            url="/vbuyer/mrp_requests/new"
          />

          <RequestFormButton
            icon={mdiArrowExpand}
            isReact={false}
            name="Material Extension"
            tooltip={t(
              "mmRequests.createNewRequest.tooltips.materialExtension",
            )}
            url="/vbuyer/material_extension_requests/new"
          />

          <RequestFormButton
            icon={mdiCurrencyUsd}
            name="Valoration"
            tooltip={t("mmRequests.createNewRequest.tooltips.valoration")}
            url="/vbuyer/valoration_requests/new"
          />

          {/* <RequestFormButton
            icon={mdiFileDocumentRefreshOutline}
            name="RSPS 3"
            tooltip={t("mmRequests.createNewRequest.tooltips.rsps3")}
            url="/vbuyer/rsps3_requests/new"
          /> */}

          <RequestFormButton
            icon={mdiInvoiceImportOutline}
            name="DUIMP"
            tooltip={t("mmRequests.createNewRequest.tooltips.duimp")}
            url="/vbuyer/duimp_requests/new"
          />
        </Grid>
      </Paper>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/mm_requests/create_new_requests",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Create Material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: CreateNewRequest,
});
